'use client'

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Pencil } from 'lucide-react'
import { CustomerDetail, useUpdateCustomerCRM } from '@/lib/api/customers'
import { CustomerEditForm, type CustomerEditFormData } from './customer-edit-form'

interface CustomerEditDialogProps {
	customer: CustomerDetail
}

/**
 * Dialog popup wrapping the customer edit form
 */
export function CustomerEditDialog({ customer }: CustomerEditDialogProps) {
	const { t } = useTranslation('customers')
	const [open, setOpen] = useState(false)
	const mutation = useUpdateCustomerCRM()

	const handleSubmit = useCallback(
		(data: CustomerEditFormData) => {
			mutation.mutate(
				{
					customerId: customer.id,
					...data,
				},
				{ onSuccess: () => setOpen(false) },
			)
		},
		[customer.id, mutation],
	)

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm">
					<Pencil className="mr-2 h-4 w-4" />
					{t('common:edit')}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{t('editCustomer')}</DialogTitle>
					<DialogDescription>{t('editCustomerDescription')}</DialogDescription>
				</DialogHeader>
				<ScrollArea className="max-h-[70vh] pr-4">
					<CustomerEditForm
						customer={customer}
						onSubmit={handleSubmit}
						isLoading={mutation.isPending}
					/>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	)
}
